import React from 'react';
import { checkAllHealth } from '../api/health';

export default function HealthPanel() {
  const [health, setHealth] = React.useState({ auth: null, registry: null, telem: null, diags: null });
  const [loading, setLoading] = React.useState(true);
  const [lastCheck, setLastCheck] = React.useState(null);

  const loadHealth = React.useCallback(async () => {
    setLoading(true);
    try {
      const results = await checkAllHealth();
      setHealth(results);
      setLastCheck(new Date());
    } catch (err) {
      console.error('Health check failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadHealth();
    const interval = setInterval(loadHealth, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [loadHealth]);

  const getStatusBadgeClass = (status) => {
    if (status === 'healthy') return 'badge-healthy';
    if (status === 'unhealthy') return 'badge-unhealthy';
    return 'badge-unreachable';
  };

  const getStatusIcon = (status) => {
    if (status === 'healthy') return '✓';
    if (status === 'unhealthy') return '⚠';
    return '✗';
  };

  if (loading && !health.auth && !health.registry && !health.telem && !health.diags) {
    return <div className="loading">Checking service health...</div>;
  }

  return (
    <div className="health-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.75rem', margin: 0 }}>Service Health</h2>
        <div>
          <button className="btn-secondary" onClick={loadHealth} disabled={loading}>
            {loading ? 'Checking...' : 'Refresh'}
          </button>
          {lastCheck && (
            <span style={{ marginLeft: '1rem', fontSize: '0.85rem', color: '#666' }}>
              Last check: {lastCheck.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      <div className="health-grid">
        {/* Auth Service */}
        <div className="health-card">
          <div className="health-card-header">
            <h3>vehicle-auth</h3>
            <span className={`health-badge ${getStatusBadgeClass(health.auth?.status)}`}>
              {getStatusIcon(health.auth?.status)} {health.auth?.status || 'unknown'}
            </span>
          </div>
          <div className="health-card-body">
            <div className="health-detail">
              <span className="health-label">Service:</span>
              <span className="health-value">Authentication Service</span>
            </div>
            <div className="health-detail">
              <span className="health-label">Endpoint:</span>
              <span className="health-value">/api/vehicle-auth/health</span>
            </div>
            {health.auth?.statusCode && (
              <div className="health-detail">
                <span className="health-label">Status Code:</span>
                <span className="health-value">{health.auth.statusCode}</span>
              </div>
            )}
            {health.auth?.error && (
              <div className="health-detail">
                <span className="health-label">Error:</span>
                <span className="health-value error-text">{health.auth.error}</span>
              </div>
            )}
          </div>
        </div>

        {/* Registry Service */}
        <div className="health-card">
          <div className="health-card-header">
            <h3>vehicle-registry</h3>
            <span className={`health-badge ${getStatusBadgeClass(health.registry?.status)}`}>
              {getStatusIcon(health.registry?.status)} {health.registry?.status || 'unknown'}
            </span>
          </div>
          <div className="health-card-body">
            <div className="health-detail">
              <span className="health-label">Service:</span>
              <span className="health-value">Vehicle Registry Service</span>
            </div>
            <div className="health-detail">
              <span className="health-label">Endpoint:</span>
              <span className="health-value">/api/vehicle-registry/health</span>
            </div>
            {health.registry?.statusCode && (
              <div className="health-detail">
                <span className="health-label">Status Code:</span>
                <span className="health-value">{health.registry.statusCode}</span>
              </div>
            )}
            {health.registry?.error && (
              <div className="health-detail">
                <span className="health-label">Error:</span>
                <span className="health-value error-text">{health.registry.error}</span>
              </div>
            )}
          </div>
        </div>

        {/* Telemetry Service */}
        <div className="health-card">
          <div className="health-card-header">
            <h3>vehicle-telem</h3>
            <span className={`health-badge ${getStatusBadgeClass(health.telem?.status)}`}>
              {getStatusIcon(health.telem?.status)} {health.telem?.status || 'unknown'}
            </span>
          </div>
          <div className="health-card-body">
            <div className="health-detail">
              <span className="health-label">Service:</span>
              <span className="health-value">Vehicle Telemetry Service</span>
            </div>
            <div className="health-detail">
              <span className="health-label">Endpoint:</span>
              <span className="health-value">/api/vehicle-telem/health</span>
            </div>
            {health.telem?.statusCode && (
              <div className="health-detail">
                <span className="health-label">Status Code:</span>
                <span className="health-value">{health.telem.statusCode}</span>
              </div>
            )}
            {health.telem?.error && (
              <div className="health-detail">
                <span className="health-label">Error:</span>
                <span className="health-value error-text">{health.telem.error}</span>
              </div>
            )}
          </div>
        </div>

        {/* Diagnostics Service */}
        <div className="health-card">
          <div className="health-card-header">
            <h3>vehicle-diags</h3>
            <span className={`health-badge ${getStatusBadgeClass(health.diags?.status)}`}>
              {getStatusIcon(health.diags?.status)} {health.diags?.status || 'unknown'}
            </span>
          </div>
          <div className="health-card-body">
            <div className="health-detail">
              <span className="health-label">Service:</span>
              <span className="health-value">Vehicle Diagnostics Service</span>
            </div>
            <div className="health-detail">
              <span className="health-label">Endpoint:</span>
              <span className="health-value">/api/vehicle-diags/health</span>
            </div>
            {health.diags?.statusCode && (
              <div className="health-detail">
                <span className="health-label">Status Code:</span>
                <span className="health-value">{health.diags.statusCode}</span>
              </div>
            )}
            {health.diags?.error && (
              <div className="health-detail">
                <span className="health-label">Error:</span>
                <span className="health-value error-text">{health.diags.error}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="health-info" style={{ marginTop: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
        <h4 style={{ marginBottom: '0.5rem', fontSize: '1rem' }}>About Health Checks</h4>
        <ul style={{ margin: 0, paddingLeft: '1.5rem', fontSize: '0.9rem', color: '#666' }}>
          <li><strong>healthy</strong>: Service is responding correctly</li>
          <li><strong>unhealthy</strong>: Service responded but with error status</li>
          <li><strong>unreachable</strong>: Service did not respond (timeout or network error)</li>
        </ul>
      </div>
    </div>
  );
}
